import { css } from '@emotion/react'
import { ConsultaSeguimentoArrowDown } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoArrowDown'
import { ConsultaSeguimentoIndicatorHelper } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoPerguntaHeader } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeaderTitle'
import { PerguntaSimNaoRadioGroupBoxField } from '@mchat-rf/consulta-seguimento/components/PerguntaSimNaoRadioGroupBoxField'
import { PerguntaSimNaoRadioListField } from '@mchat-rf/consulta-seguimento/components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '@mchat-rf/consulta-seguimento/consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { useConsultaSeguimentoAutoScroll } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoAutoScroll'
import { useConsultaSeguimentoSetResultado } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoSetResultado'
import { Flow, isEmpty, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { RefObject } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import {
  ConsultaSeguimentoPergunta20ExemplosEnum,
  ConsultaSeguimentoPergunta20Model,
} from './model-consultaSeguimentoPergunta20'
import { PERGUNTA_20_SCROLL_CONFIG } from './scrollConfig-consultaSeguimentoPergunta20'
import {
  calculateResultadoPergunta20,
  conditionToCalculatePergunta20,
  hasPergunta20ExemplosAnySim,
} from './utils-consultaSeguimentoPergunta20'

interface ConsultaSeguimentoPergunta20FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta20Model>
  containerRef: RefObject<HTMLDivElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
}

export function ConsultaSeguimentoPergunta20Field(props: Readonly<ConsultaSeguimentoPergunta20FieldProps>) {
  const { name, containerRef, goNextButtonRef } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta20Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta20Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta20,
    calculateResultado: calculateResultadoPergunta20,
  })

  const { getId, triggerScrollFor } = useConsultaSeguimentoAutoScroll<ConsultaSeguimentoPergunta20Model>({
    name,
    containerRef,
    goNextButtonRef,
    scrollConfig: PERGUNTA_20_SCROLL_CONFIG,
  })

  const hasExemplosAnySim = hasPergunta20ExemplosAnySim(value)
  const isPerguntaHeaderSim = value.PERGUNTA_20_HEADER === SimNaoEnum.SIM
  const isPerguntaHeaderNao = value.PERGUNTA_20_HEADER === SimNaoEnum.NAO
  const isGostaDePularOuBalancarNao = value.PERGUNTA_20_GOSTA_DE_PULAR_OU_BALANCAR === SimNaoEnum.NAO

  const textResultadoPassou =
    hasExemplosAnySim && (isPerguntaHeaderNao || (isPerguntaHeaderSim && isGostaDePularOuBalancarNao))
      ? 'Sim para qualquer exemplo específico (ou se “outro” é uma resposta positiva):'
      : ''

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_20}
            title={<ConsultaSeguimentoPerguntaHeaderTitle sufix=' gosta de atividades de movimento?' />}
            status={value.status}
            field={
              <SimNaoSwitchButtonField
                name={name.PERGUNTA_20_HEADER}
                onChange={() => triggerScrollFor('PERGUNTA_20_HEADER')}
              />
            }
          />
        )}
        {!isEmpty(value.PERGUNTA_20_HEADER) && (
          <VFlow vSpacing={0} id={getId('PERGUNTA_20_GOSTA_DE_PULAR_OU_BALANCAR')}>
            <ConsultaSeguimentoIndicatorStart />
            {value.PERGUNTA_20_HEADER === SimNaoEnum.SIM && (
              <PerguntaSimNaoRadioGroupBoxField
                name={name.PERGUNTA_20_GOSTA_DE_PULAR_OU_BALANCAR}
                label='Ele gosta quando alguém o faz pular ou balançar?'
                required
                onChange={() => triggerScrollFor('PERGUNTA_20_GOSTA_DE_PULAR_OU_BALANCAR')}
              />
            )}
            {(value.PERGUNTA_20_HEADER === SimNaoEnum.NAO || isGostaDePularOuBalancarNao) && (
              <Flow direction='vertical' gap={0} id={getId('PERGUNTA_20_EXEMPLOS')}>
                {value.PERGUNTA_20_HEADER === SimNaoEnum.SIM && <ConsultaSeguimentoArrowDown />}
                <VFlow>
                  <ConsultaSeguimentoIndicatorHelper
                    title='Quando você balança ou joga ele para o alto, como ele reage?'
                    subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                  />
                  <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta20ExemplosEnum>
                    name={name.PERGUNTA_20_EXEMPLOS}
                    nameOutro={name.PERGUNTA_20_OUTRO}
                    label='O seu filho...'
                    description='Responda os exemplos até pontuar “Passou” ou “Falhou”'
                    options={PERGUNTA_20_EXEMPLOS_OPTIONS}
                    hideOutrosFieldRadioOptions
                    required
                    onChange={() => triggerScrollFor('PERGUNTA_20_EXEMPLOS')}
                    onOutroFieldBlur={() => triggerScrollFor('PERGUNTA_20_EXEMPLOS')}
                  />
                </VFlow>
              </Flow>
            )}
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          resultado={value.resultado}
          text={{
            FALHOU: 'Não para todos:',
            PASSOU: textResultadoPassou,
          }}
          style={styles.resultado}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_20_EXEMPLOS_OPTIONS: RadioOption<ConsultaSeguimentoPergunta20ExemplosEnum>[] = [
  {
    label: 'Ri ou dá gargalhadas?',
    value: ConsultaSeguimentoPergunta20ExemplosEnum.RI_OU_GARGALHA,
  },
  {
    label: 'Fala ou emite algum som?',
    value: ConsultaSeguimentoPergunta20ExemplosEnum.FALA_OU_EMITE_SOM,
  },
  {
    label: 'Pede mais, esticando seus braços?',
    value: ConsultaSeguimentoPergunta20ExemplosEnum.PEDE_MAIS,
  },
]

const styles = {
  resultado: css`
    max-width: 17rem;
  `,
}
